import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search } from 'lucide-react';
import glossaryTerms from '../data/glossaryTerms';
import { Helmet } from 'react-helmet-async';

const GlossaryPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const filteredTerms = glossaryTerms.filter(term =>
    term.term.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Marketing Terms Glossary: Complete Digital Marketing Dictionary & Definitions</title>
        <meta name="description" content="Master marketing terminology with our comprehensive glossary. 500+ marketing terms explained, from A/B testing to Zero-party data. Free marketing dictionary for professionals." />
        <meta name="keywords" content="marketing glossary, marketing terms, digital marketing dictionary, marketing definitions, marketing terminology, marketing metrics glossary, marketing analytics terms" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.marketingcalculatorhub.com/glossary" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.marketingcalculatorhub.com/glossary" />
        <meta property="og:title" content="Complete Marketing Terms Glossary & Digital Marketing Dictionary" />
        <meta property="og:description" content="Your comprehensive guide to marketing terminology. 500+ marketing terms explained simply. Perfect for marketers, students, and business professionals." />
        <meta property="og:image" content="https://www.marketingcalculatorhub.com/glossary-preview.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.marketingcalculatorhub.com/glossary" />
        <meta property="twitter:title" content="Complete Marketing Terms Glossary & Digital Marketing Dictionary" />
        <meta property="twitter:description" content="Your comprehensive guide to marketing terminology. 500+ marketing terms explained simply. Perfect for marketers, students, and business professionals." />
        <meta property="twitter:image" content="https://www.marketingcalculatorhub.com/glossary-preview.jpg" />

        {/* Additional Meta Tags */}
        <meta name="author" content="Marketing Calculator Hub" />
        <meta name="robots" content="index, follow" />
        <meta property="article:modified_time" content="2024-10-23" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            "name": "Marketing Terms Glossary",
            "description": "Comprehensive glossary of marketing terms and definitions covering digital marketing, analytics, and business metrics",
            "url": "https://www.marketingcalculatorhub.com/glossary",
            "numberOfItems": "500+",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.marketingcalculatorhub.com/glossary"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Marketing Calculator Hub",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.marketingcalculatorhub.com/logo.png"
              }
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.marketingcalculatorhub.com"
              }, {
                "@type": "ListItem",
                "position": 2,
                "name": "Marketing Terms Glossary",
                "item": "https://www.marketingcalculatorhub.com/glossary"
              }]
            }
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <BookOpen className="w-8 h-8 mr-2 text-indigo-600" />
        Marketing Glossary
      </h1>
      
      <div className="mb-8 relative">
        <input
          type="text"
          placeholder="Search terms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      </div>

      {searchTerm ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTerms.map((term, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">{term.term}</h2>
              <p className="text-gray-600 mb-4">{term.definition}</p>
              <Link to={`/glossary-term/${term.term.toLowerCase().replace(/\s+/g, '-')}`} className="text-indigo-600 hover:text-indigo-800">
                Learn more
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-13 gap-4 mb-8">
            {alphabet.map((letter) => (
              <Link
                key={letter}
                to={`/glossary/${letter.toLowerCase()}`}
                className="text-center text-xl font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                {letter}
              </Link>
            ))}
          </div>

          <p className="text-gray-600 text-center">
            Click on a letter above to view terms starting with that letter, or use the search bar to find specific terms.
          </p>
        </>
      )}
    </div>
  );
};

export default GlossaryPage;
